import { useQuery } from "@tanstack/react-query";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import Button from "../../components/ui/Button/Button";
import Header from "../../components/ui/Header/Header";
import { companyName } from "../../constants/constants";
import { gigsApi } from "../../lib/gigs";
import { apiErrorToMessage } from "../../utils/ApiUtils";
import { formatEstimatedDuration } from "../../utils/DateUtils";
import { formatRoundedGigCompensation } from "../../utils/GigCompensationUtils";
import { formatGigCrewSummary } from "../../utils/GigCrewUtils";
import { formatGigScheduleSummary } from "../../utils/GigScheduleUtils";
import { formatCityStateZip } from "../../utils/LocationUtils";

const PublicGigPreviewPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const gigId = searchParams.get("gigId");
  const returnPath = `${location.pathname}${location.search}`;
  const loginPath = `/login?redirect=${encodeURIComponent(returnPath)}`;
  const signupPath = "/signup";

  const previewQuery = useQuery({
    queryKey: ["public-gig-preview", gigId],
    queryFn: () => gigsApi.getPublicPreview(gigId!),
    enabled: Boolean(gigId),
    retry: false,
  });

  const gig = previewQuery.data ?? null;
  const locationLabel = gig ? formatCityStateZip({ locationType: gig.locationType, city: gig.city, state: gig.state }, "Location shared after sign-in") : "";
  const compensationLabel = gig ? formatRoundedGigCompensation(gig.paymentAmountCents, gig.volunteer) : "";
  const crewLabel = gig ? formatGigCrewSummary(gig) : "";
  const scheduleLabel = gig
    ? formatGigScheduleSummary({
      scheduleType: gig.scheduleType,
      scheduleNotes: gig.scheduleNotes,
      startTime: gig.startTime,
      endTime: gig.endTime,
      timeZone: gig.timeZone,
      preferredStartTime: gig.preferredStartTime,
      preferredEndTime: gig.preferredEndTime,
    })
    : "";

  return (
    <div className="min-h-screen bg-myteengig-background text-myteengig-text-primary">
      <Header
        sticky
        mobileMenuEnabled={false}
      />

      <main className="mx-auto w-full max-w-4xl px-4 py-8 sm:px-6 sm:py-12">
        {!gigId ? (
          <section className="rounded-lg border border-myteengig-border bg-white px-5 py-6 shadow-sm">
            <h1 className="text-2xl font-bold">Gig link unavailable</h1>
            <p className="mt-2 text-sm leading-6 text-myteengig-text-muted">
              This shared link is missing a gig id. Sign in to browse available opportunities.
            </p>
            <div className="mt-5 flex flex-col gap-3 sm:flex-row">
              <Button onClick={() => navigate(loginPath)} className="w-full sm:w-auto">
                Sign in
              </Button>
              <Button variant="outline" onClick={() => navigate(signupPath)} className="w-full sm:w-auto">
                Create an account
              </Button>
            </div>
          </section>
        ) : null}

        {previewQuery.isLoading ? (
          <section className="rounded-lg border border-myteengig-border bg-white px-5 py-6 shadow-sm">
            <p className="text-sm text-myteengig-text-muted">Loading gig preview...</p>
          </section>
        ) : null}

        {previewQuery.error ? (
          <section className="rounded-lg border border-myteengig-border bg-white px-5 py-6 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Shared gig preview</p>
            <h1 className="mt-2 text-2xl font-bold">This gig is not available</h1>
            <p className="mt-2 text-sm leading-6 text-myteengig-text-muted">
              {apiErrorToMessage(previewQuery.error, "This gig may have been filled, removed, or expired.")}
            </p>
            <div className="mt-5 flex flex-col gap-3 sm:flex-row">
              <Button onClick={() => navigate(loginPath)} className="w-full sm:w-auto">
                Sign in
              </Button>
              <Button variant="outline" onClick={() => navigate(signupPath)} className="w-full sm:w-auto">
                Create an account
              </Button>
            </div>
          </section>
        ) : null}

        {gig ? (
          <div className="space-y-5">
            <section className="rounded-lg border border-myteengig-border bg-white px-4 py-4 shadow-sm sm:px-6 sm:py-6">
              <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Shared gig preview</p>
              <div className="mt-2 flex flex-col gap-3 sm:mt-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
                <div className="min-w-0">
                  <h1 className="text-2xl font-bold tracking-tight sm:text-4xl">{gig.title}</h1>
                  <p className="mt-1 text-sm font-medium text-myteengig-text-muted sm:mt-2">{gig.category} in {locationLabel}</p>
                </div>
                <div className="flex shrink-0 flex-wrap gap-2 sm:justify-end">
                  <span className="rounded-full border border-myteengig-border bg-myteengig-background px-2.5 py-1 text-xs font-semibold sm:px-3 sm:text-sm">
                    {compensationLabel}
                  </span>
                  <span className="rounded-full border border-myteengig-border bg-myteengig-background px-2.5 py-1 text-xs font-semibold sm:px-3 sm:text-sm">
                    {formatEstimatedDuration(gig.estimatedDurationMinutes)}
                  </span>
                  <span className="rounded-full border border-myteengig-border bg-myteengig-background px-2.5 py-1 text-xs font-semibold sm:px-3 sm:text-sm">
                    {crewLabel}
                  </span>
                </div>
              </div>
            </section>

            <section className="grid gap-4 md:grid-cols-[minmax(0,1.45fr)_minmax(16rem,0.8fr)]">
              <div className="rounded-lg border border-myteengig-border bg-white px-5 py-5 shadow-sm">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-myteengig-text-muted">Preview</h2>
                <p className="mt-3 text-sm leading-7 text-myteengig-text-primary">
                  {gig.descriptionPreview || "Sign in to view this gig's full description."}
                </p>
              </div>

              <aside className="rounded-lg border border-myteengig-border bg-white px-5 py-5 shadow-sm">
                <dl className="space-y-4 text-sm">
                  <div>
                    <dt className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Timing</dt>
                    <dd className="mt-1 leading-6">{scheduleLabel}</dd>
                  </div>
                  <div>
                    <dt className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Location</dt>
                    <dd className="mt-1">{locationLabel}</dd>
                  </div>
                  <div>
                    <dt className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Crew</dt>
                    <dd className="mt-1">{crewLabel}</dd>
                  </div>
                  <div>
                    <dt className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Poster</dt>
                    <dd className="mt-1">{gig.posterIdentityVerified ? "ID verified" : "Profile visible after sign-in"}</dd>
                  </div>
                </dl>
              </aside>
            </section>

            <section className="rounded-lg border border-myteengig-border bg-white px-5 py-5 text-center shadow-sm">
              <h2 className="text-xl font-semibold">Want the full listing?</h2>
              <p className="mx-auto mt-2 max-w-2xl text-sm leading-6 text-myteengig-text-muted">
                Sign in or create an account to view full details, poster profile, photos, and application options on {companyName}.
              </p>
              <div className="mt-5 flex flex-col justify-center gap-3 sm:flex-row">
                <Button onClick={() => navigate(signupPath)} className="w-full sm:w-auto">
                  Create an account
                </Button>
                <Button variant="outline" onClick={() => navigate(loginPath)} className="w-full sm:w-auto">
                  Sign in to view more
                </Button>
              </div>
              <p className="mt-4 text-sm text-myteengig-text-muted">
                <Link to="/" className="font-semibold text-myteengig-primary underline underline-offset-2">
                  Learn about {companyName}
                </Link>
              </p>
            </section>
          </div>
        ) : null}
      </main>
    </div>
  );
};

export default PublicGigPreviewPage;
